/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { styles } from './themes/PieChartStyles.css';
import {
  getDefaultBorderColor,
  getElementBounds,
  getPieCenter,
  getRadius,
  getRenderedSlices,
  getPieChartInfo,
  getPieSliceGap,
  getSliceInfo
} from './utils';
import { getAvailSpace } from '#utils/PRIVATE_chartUtils/layoutUtils';
import { PieSlice } from './PieSlice';
import { getCenterContext } from './utils';
import { getPieChartNavUtils } from './pieNavUtils';
import { ItemInfo, usePieChartNav } from '#hooks/PRIVATE_usePieChartNav';
import { useDatatip } from '#hooks/PRIVATE_useDatatip';
import { calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { VisTabularDatatip } from '#PRIVATE_VisTabularDatatip';
import type { PieChartProps, PieChartItemData } from './pieChart.types';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { useRef } from 'preact/hooks';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { usePieChartContextMenu } from './usePieChartContextMenu';
import { layoutPieChartLabels, PieLabelInfo } from './labelUtils';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { getChartVars } from '#utils/PRIVATE_chartUtils';
import { PieChartLabel } from './PieChartLabel';

type PieChartWithDimensionsProps<K, D> = Omit<PieChartProps<K, D>, 'width' | 'height'> & {
  width: number;
  height: number;
};

const NUM_PIE_SLICE_THRESHOLD = 100;

const chartVars = getChartVars();

export function PieChartWithDimensions<
  K extends string | number,
  D extends PieChartItemData<K> = PieChartItemData<K>
>({
  width,
  height,
  testId,
  innerRadius = 0,
  contextMenuConfig,
  drilling = 'off',
  selectionMode = 'none',
  onItemDrill,
  onItemHover,
  onItemFocus,
  ...props
}: PieChartWithDimensionsProps<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const { resolvedVars, cssContent } = useCssVars(chartVars);
  const colors = Array.from(Array(12)).map((_, index) => {
    return resolvedVars[`color${index + 1}`];
  });
  
  let data = props.data.filter(item => item.value > 0);
  if (isRtl) {
    data = data.slice().reverse();
  }
  const { totalValue, isLabelOutside } = getPieChartInfo(data);
  const sliceInfos = getSliceInfo(data, totalValue, colors, isRtl);
  const hasData = data.length > 0;

  // default data item gaps is 0.5. TODO: replace 0.5 with pie dataItemGaps/pieSliceGaps
  const pieSliceGap = getPieSliceGap(0.5);
  const hasLargeSeriesCount = data.length > NUM_PIE_SLICE_THRESHOLD;

  const testIdProps = useTestId(testId);
  const availSpace = getAvailSpace(width, height, false);
  const { cx, cy } = getPieCenter(availSpace);
  const radius = getRadius(availSpace, isLabelOutside);
  const ir = innerRadius * radius;
  const renderedSlices = getRenderedSlices(sliceInfos, hasLargeSeriesCount, radius);
  const { getDetailFromInfo, getNextChartItem } = getPieChartNavUtils<K, D>(
    data,
    direction === 'rtl'
  );

  const { textMeasureContent, getTextDimensions } = useTextDimensions();

  const supportsSelection = !!(selectionMode && selectionMode !== 'none');
  const supportsDrill = drilling === 'on';
  // const isReadOnly = !supportsSelection && !supportsDrill && onItemHover === null && onItemFocus === null;
  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures: false,
    rootRef
  });

  const { activeId, focusedItemInfo, hoveredItemInfo, eventsProps, onContextMenuDismissed } =
    usePieChartNav<K, D>(touchResponse, getDetailFromInfo, getNextChartItem, drilling, onItemDrill);
  const activeItem = focusedItemInfo.isFocusVisible ? focusedItemInfo : hoveredItemInfo;
  const activeData = activeItem ? data[renderedSlices[activeItem.itemIndex].index] : undefined;

  const defaultDatatip = (activeData?: PieChartItemData<K>, activeItem?: ItemInfo) => {
    if (!activeData) {
      return {
        content: undefined,
        borderColor: undefined,
        defaultContainer: undefined
      };
    }
    if (activeData.accessibleLabel) {
      return {
        content: activeData.accessibleLabel,
        borderColor: getDefaultBorderColor(colors, activeItem, activeData),
        defaultContainer: 'enabled' as const
      };
    }
    return {
      content: (
        <VisTabularDatatip
          tableData={[
            {
              key: 'Value',
              value: activeData.value
            }
          ]}
        />
      ),
      borderColor: getDefaultBorderColor(colors, activeItem, activeData),
      defaultContainer: 'enabled' as const
    };
  };

  const {
    content: datatipString,
    borderColor,
    defaultContainer
  } = activeData && props.datatip
    ? props.datatip({ data: activeData })
    : defaultDatatip(activeData, activeItem);

  const anchor = focusedItemInfo.isFocusVisible ? 'element' : 'pointer';

  const elementBounds = activeItem?.isFocusVisible
    ? getElementBounds(renderedSlices, cx, cy, ir, radius, activeItem)
    : undefined;

  const offset = calculateOffset(isRtl, width, elementBounds);
  const { datatipContent, datatipProps } = useDatatip({
    content: datatipString,
    offset,
    anchor,
    placement: 'top-start',
    borderColor,
    touchResponse,
    defaultContainer
  });

  datatipProps['aria-describedby'] = merge([
    props['aria-describedby'],
    datatipProps['aria-describedby']
  ]);

  const { contextMenuContent, contextMenuProps } = usePieChartContextMenu<K, D>({
    offset,
    rootRef,
    contextMenuConfig: hasData ? contextMenuConfig : undefined,
    onContextMenuDismissed,
    focusedItemInfo,
    items: sliceInfos,
    width,
    isRtl
  });

  const mergedProps = mergeProps(eventsProps, datatipProps, contextMenuProps);

  let insideLabels: PieLabelInfo<K>[] = [];
  let rightLabels: PieLabelInfo<K>[] = [];
  let leftLabels: PieLabelInfo<K>[] = [];

  const defaultFontSize = resolvedVars['labelFontSize'];

  if (getTextDimensions) {
    const labels = layoutPieChartLabels(
      renderedSlices,
      { cx, cy },
      ir,
      radius,
      availSpace,
      getTextDimensions,
      defaultFontSize
    );
    insideLabels = labels.insideLabels;
    rightLabels = labels.rightLabels;
    leftLabels = labels.leftLabels;
  }

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-activedescendant={activeId}
      role="application"
      {...mergedProps}
      {...testIdProps}
      className={styles.chart}
      style={touchResponseStyle}>
      {renderedSlices.length > 0 && hasData && ( //className={styles.svg}
        <svg className={styles.svg}>
          {renderedSlices.map((item, index: number) => {
            return (
              <PieSlice
                isInteractive={supportsSelection || supportsDrill}
                cx={cx}
                cy={cy}
                itemIndex={index}
                id={activeItem?.itemIndex === index ? activeId : undefined}
                isFocused={focusedItemInfo?.itemIndex === index && focusedItemInfo?.isFocusVisible}
                isHovered={hoveredItemInfo?.itemIndex === index}
                r={radius}
                startAngle={item.startAngle}
                gap={pieSliceGap}
                innerRadius={ir}
                angleExtent={item.angleExtent}
                color={item.color}
                key={item.id}
                accessibleLabel={item.accessibleLabel}
              />
            );
          })}
          {insideLabels?.map((v) => {
            return (
              <PieChartLabel
                isRtl={isRtl}
                pieLabelInfo={v}
                labelPos="inside"
                fontSize={defaultFontSize}
              />
            );
          })}
          {leftLabels?.map((v) => {
            return (
              <PieChartLabel
                isRtl={isRtl}
                pieLabelInfo={v}
                labelPos="left"
                fontSize={defaultFontSize}
              />
            );
          })}
          {rightLabels?.map((v) => {
            return (
              <PieChartLabel
                isRtl={isRtl}
                pieLabelInfo={v}
                labelPos="right"
                fontSize={defaultFontSize}
              />
            );
          })}
        </svg>
      )}
      {props.children?.(getCenterContext(cx, cy, ir))}
      {datatipContent}
      {cssContent}
      {contextMenuContent}
      {textMeasureContent}
    </div>
  );
}
